import React, { FC, useMemo } from 'react';

import { TradingActivitiesReportAggregate, TradingActivitiesReportData } from '../../TradingActivitiesReportModel';

import { DataTable, ColumnConfig } from '../../../DataTable';

interface Props {
  rows: TradingActivitiesReportData[];
  aggregate?: TradingActivitiesReportAggregate;
  columns: ColumnConfig<TradingActivitiesReportData>[];
  selectedColumns: string[];
  translation: any;
  onSelectColumns(columns: string[]): void;
  onPageChange: (pageNumber: number) => void;
}

type TradingActivitiesReportTableRow = TradingActivitiesReportData & {
  isAggregate?: boolean;
};

export const ReportTable: FC<Props> = ({ rows, aggregate, columns, selectedColumns, onPageChange, translation, onSelectColumns }) => {
  const data = useMemo<TradingActivitiesReportTableRow[]>(() => {
    const hasTotals =
      aggregate?.totalVolumeUSD != null &&
      aggregate?.totalCommissionUSD != null &&
      aggregate?.totalProfitUSD != null;

    const aggregateRow: TradingActivitiesReportTableRow | null =
      aggregate && hasTotals
        ? {
            id: -1,
            isAggregate: true,
            order: 0,
            login: 0,
            partnershipCode: '',
            openTime: '',
            type: '',
            symbol: '',
            lot: aggregate.totalLot,
            volumeUSD: aggregate.totalVolumeUSD,
            openPrice: 0,
            closeTime: '',
            closePrice: 0,
            sl: 0,
            tp: 0,
            commissionUSD: aggregate.totalCommissionUSD,
            swapUSD: 0,
            profitUSD: aggregate.totalProfitUSD,
          }
        : null;

    return aggregateRow ? [aggregateRow, ...rows] : rows;
  }, [aggregate, rows]);

  // Wrap parent format functions with isAggregate-aware display logic
  const displayColumns = useMemo(
    (): ColumnConfig<TradingActivitiesReportTableRow>[] =>
      columns.map((col) => ({
        ...col,
        format: (value: any, row: TradingActivitiesReportTableRow) => {
          if (row.isAggregate) {
            if (col.key === 'order') return translation.total || 'Total';

            if (['lot', 'volumeUSD', 'commissionUSD', 'profitUSD'].includes(col.key)) {
              return col.format ? col.format(value, row) : String(value ?? '-');
            }

            return '';
          }

          return col.format ? col.format(value, row) : (value ?? '-');
        },
      })),
    [columns, translation.total],
  );

  return (
    <DataTable
      columns={displayColumns}
      data={data}
      translation={translation}
      columnSelect
      selectedColumns={selectedColumns}
      onSelectColumns={onSelectColumns}
      pagination="pages"
      sortable
      rowsPerPage={500}
      onPageChange={onPageChange}
      emptyMessage={
        translation.text_6448 ||
        translation.reports_EmptyTableMessage
      }
    />
  );
};
